import { useContext, useState, useEffect } from 'react';
import {
    Card,
    Grid,
    Table,
    TableBody,
    TableContainer,
    TableRow,
    Typography,
    Box
} from '@mui/material';
import {
    Dropdown,
    DropDownChoices,
    DropdownType
} from '@/components/shared/Dropdown';
import { hoursOrMinutes, useFormatting } from '@/utils/formatting';
import { useTranslation } from 'react-i18next';
import { ShiftStatusWithdrawAllowed } from '@instapaid/shared-logic';
import { UserContext } from '@/contexts/user/UserContext';
import { Dayjs } from 'dayjs';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import {
    TableCell,
    TableCellFirstCell,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellFirstCell
} from '@/components/shared/styles/Table';
import Chip from '@/components/shared/Chip';
import { Tip } from '@/services/PaymentsServiceV2';
import { useGetUserPastTips } from '@/services/paymentsQuery';
import CardHeader from '@/components/shared/CardHeader';
import { Status } from '@/pages/users/UserTransactions';
import { XSMedium } from '@/typography/Typography';
import {
    TablePagination,
    usePagination
} from '@/components/shared/TablePagination';

export type TipsTableProps = {
    userId: string;
    payrollPeriod: string;
    tips?: Tip[];
    payoutType: string;
    companyName: string;
    heading?: string;
    total?: number;
    totalHours?: number;
    payrollPeriodStart?: Dayjs;
    payrollPeriodEnd?: Dayjs;
};

export const TipsTable = ({
    tips: initialTips,
    total: initialTotal,
    totalHours: initialTotalHours,
    payoutType,
    heading: _heading,
    companyName,
    userId,
    payrollPeriodStart: _payrollPeriodStart,
    payrollPeriodEnd: _payrollPeriodEnd
}: TipsTableProps) => {
    const { company: adminCompany } = useContext(UserContext);
    const { getBalanceLabelV2, toDecimal } = useFormatting();

    const { t } = useTranslation();
    const { formatDate, formatDateTimeToTime } = useFormatting();
    const dispatch = useAppDispatch();

    const [filterType, setFilterType] = useState<'current' | 'history'>(
        'current'
    );
    const [tips, setTips] = useState<Tip[] | undefined>(initialTips);
    const [total, setTotal] = useState<number | undefined>(initialTotal);
    const [totalHours, setTotalHours] = useState<number | undefined>(
        initialTotalHours
    );
    const [historyPage, setHistoryPage] = useState(0);
    const [historyTotalCount, setHistoryTotalCount] = useState(0);

    const { rowsPerPage, setRowsPerPage } = usePagination();

    const { data: pastTipsData, isPending: isPastTipsPending } =
        useGetUserPastTips({
            userId,
            startDate: undefined,
            endDate: undefined,
            offset: rowsPerPage === -1 ? undefined : historyPage * rowsPerPage,
            limit: rowsPerPage === -1 ? undefined : rowsPerPage,
            enabled: filterType === 'history'
        });

    useEffect(() => {
        // Only show loader when actively fetching history data
        if (filterType === 'history') {
            dispatch(setLoader(isPastTipsPending));
        }
    }, [isPastTipsPending, dispatch, filterType]);

    const filterChoices: DropDownChoices<'current' | 'history'>[] = [
        { label: 'Current Payroll', value: 'current' },
        { label: 'History', value: 'history' }
    ];

    useEffect(() => {
        if (filterType === 'current') {
            setTips(initialTips);
            setTotal(initialTotal);
            setTotalHours(initialTotalHours);
        } else if (pastTipsData?.data) {
            setTips(pastTipsData.data.tips);
            setTotal(pastTipsData.data.totalAmount);
            setTotalHours(pastTipsData.data.totalHours);
            setHistoryTotalCount(pastTipsData.data.total || 0);
        }
    }, [
        filterType,
        initialTips,
        initialTotal,
        initialTotalHours,
        pastTipsData
    ]);

    const handleFilterChange = (value: 'current' | 'history') => {
        setFilterType(value);
        if (value === 'history') {
            setHistoryPage(0);
        }
    };

    return (
        <>
            <Card>
                <CardHeader
                    label={companyName}
                    chip={
                        <Chip
                            label={
                                <XSMedium>
                                    {t('payout_type', {
                                        payoutType
                                    })}
                                </XSMedium>
                            }
                            icon={<span></span>}
                            color={'success'}
                        />
                    }
                    actions={
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography fontSize="14px" fontWeight={600}>
                                Filter by:
                            </Typography>
                            <Box minWidth="200px">
                                <Dropdown
                                    choices={filterChoices}
                                    value={filterType}
                                    onChange={handleFilterChange}
                                    type={DropdownType.Single}
                                />
                            </Box>
                        </Box>
                    }
                />
                <Grid md={12}></Grid>

                {tips && tips.length > 0 ? (
                    <>
                        <TableContainer>
                            <Table aria-label="tips table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCellFirstCell>
                                            Date
                                        </TableHeaderCellFirstCell>
                                        <TableHeaderCellFirstCell>
                                            {t('distributed_at')}
                                        </TableHeaderCellFirstCell>
                                        <TableHeaderCell>Time</TableHeaderCell>
                                        <TableHeaderCell>Pause</TableHeaderCell>
                                        <TableHeaderCell>
                                            Worked Hours
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Status
                                        </TableHeaderCell>
                                        <TableHeaderCell align="right">
                                            Tips earned
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {tips?.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCellFirstCell>
                                                {formatDate(
                                                    row.startDateTime,
                                                    undefined,
                                                    true
                                                )}
                                            </TableCellFirstCell>
                                            <TableCellFirstCell>
                                                {formatDate(row.createdAt)}
                                            </TableCellFirstCell>
                                            <TableCell>
                                                {row.startDateTime &&
                                                row.endDateTime ? (
                                                    <>
                                                        {formatDateTimeToTime(
                                                            row.startDateTime
                                                        )}{' '}
                                                        -{' '}
                                                        {formatDateTimeToTime(
                                                            row.endDateTime
                                                        )}
                                                    </>
                                                ) : (
                                                    <>Period not reported</>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {hoursOrMinutes(row.break)}{' '}
                                            </TableCell>

                                            <TableCell
                                                style={{ width: '140px' }}
                                            >
                                                {toDecimal(row.duration, 1)}
                                            </TableCell>

                                            <TableCell
                                                style={{ width: '138px' }}
                                            >
                                                {row.status && (
                                                    <Status
                                                        status={row.status}
                                                    />
                                                )}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                // color={!row.availableForPayout}
                                            >
                                                {getBalanceLabelV2(
                                                    row.amount,
                                                    adminCompany.currency,
                                                    { showNegativeStyle: true }
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                {filterType === 'current' && (
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell>
                                                You earned
                                            </TableHeaderCell>
                                            <TableHeaderCell></TableHeaderCell>
                                            <TableHeaderCell></TableHeaderCell>
                                            <TableHeaderCell></TableHeaderCell>
                                            <TableHeaderCell
                                                style={{ width: '80px' }}
                                            >
                                                {totalHours}
                                            </TableHeaderCell>
                                            <TableHeaderCell></TableHeaderCell>
                                            <TableHeaderCell align="right">
                                                {getBalanceLabelV2(
                                                    total,
                                                    adminCompany.currency,
                                                    { showNegativeStyle: true }
                                                )}
                                            </TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                )}
                            </Table>
                        </TableContainer>
                        {filterType === 'history' && (
                            <TablePagination
                                memoryMode={true}
                                page={historyPage}
                                rowsPerPageOptions={[
                                    { label: '16', value: 16 },
                                    { label: '24', value: 24 },
                                    { label: '32', value: 32 },
                                    { label: 'All', value: -1 }
                                ]}
                                count={historyTotalCount}
                                rowsPerPage={rowsPerPage}
                                resetPage={() => setHistoryPage(0)}
                                onPageChange={(newPage) => {
                                    if (typeof newPage === 'number') {
                                        setHistoryPage(newPage);
                                    }
                                }}
                                onRowsPerPageChange={(newRowsPerPage) => {
                                    setRowsPerPage(newRowsPerPage as number);
                                    setHistoryPage(0);
                                }}
                            />
                        )}
                    </>
                ) : (
                    <Typography textAlign={'center'} sx={{ py: 4 }}>
                        {t('no_available_tips')}
                    </Typography>
                )}
            </Card>
        </>
    );
};
